// src/pages/departments/CyberSecurity.js

import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Award,
  BadgeCheck,
  BookOpen,
  BookOpenCheck,
  BriefcaseBusiness,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Cloud,
  Code2,
  Database,
  FileKey2,
  GraduationCap,
  HardDrive,
  Lock,
  Mail,
  MapPin,
  Maximize2,
  Network,
  Phone,
  Search,
  Server,
  Shield,
  Users,
  Users2,
  X,
} from "lucide-react";

/* =========================================================
   SHARED DIVIDER — SAME VISUAL LANGUAGE AS NETWORKING PAGE
========================================================= */

const Divider = ({ delay = 0 }) => (
  <motion.div
    initial={{ scaleX: 0 }}
    whileInView={{ scaleX: 1 }}
    transition={{ duration: 0.8, delay }}
    viewport={{ once: true, amount: 0.1 }}
    className="origin-left h-1 bg-gradient-to-r from-blue-700 to-blue-400 rounded-full my-8"
  />
);

/* =========================================================
   STAFF DATA
   Keep unknown academic details empty until confirmed.
========================================================= */

const staffList = [
  {
    id: 1,
    name: "Dr. Mamman Adamu",
    role: "Dean",
    image: "/images/staff1.jpg",
    badge: "Dean",
    specialization: "",
    qualifications: [],
    researchInterests: [],
    professionalMemberships: [],
    achievements: [],
    email: "",
    academicProfile:
      "Additional academic profile information will be updated when supplied by the department.",
  },
  {
    id: 2,
    name: "Mrs. Zainab Ndatsu",
    role: "Head of Department",
    image: "/images/hodcy.jpeg",
    badge: "HOD",
    specialization: "",
    qualifications: [],
    researchInterests: [],
    professionalMemberships: [],
    achievements: [],
    email: "",
    academicProfile:
      "Additional academic qualifications, research interests, professional memberships and achievements will be added when supplied by the department.",
  },
  {
    id: 3,
    name: "Mr. El-Mamud Mohammad",
    role: "Exam Officer",
    image: "/images/staff3.jpg",
    badge: "Exam Officer",
    specialization: "",
    qualifications: [],
    researchInterests: [],
    professionalMemberships: [],
    achievements: [],
    email: "",
    academicProfile:
      "Additional academic profile information will be updated when supplied by the department.",
  },
  {
    id: 4,
    name: "Chief Ndubuisi A. Samuel",
    role: "Academic Staff",
    image: "/images/staff4.jpg",
    badge: "Staff",
    specialization: "",
    qualifications: [],
    researchInterests: [],
    professionalMemberships: [],
    achievements: [],
    email: "",
    academicProfile:
      "Additional academic profile information will be updated when supplied by the department.",
  },
  {
    id: 5,
    name: "Mr. Jibril Mohammed",
    role: "Academic Staff",
    image: "/images/staff5.jpg",
    badge: "Staff",
    specialization: "",
    qualifications: [],
    researchInterests: [],
    professionalMemberships: [],
    achievements: [],
    email: "",
    academicProfile:
      "Additional academic profile information will be updated when supplied by the department.",
  },
  {
    id: 6,
    name: "Mrs. Ogala Faith A.",
    role: "Academic Staff",
    image: "/images/staff6.jpg",
    badge: "Staff",
    specialization: "",
    qualifications: [],
    researchInterests: [],
    professionalMemberships: [],
    achievements: [],
    email: "",
    academicProfile:
      "Additional academic profile information will be updated when supplied by the department.",
  },
  {
    id: 7,
    name: "Mr. Koce A. Kenneth",
    role: "Academic Staff",
    image: "/images/staff7.jpg",
    badge: "Staff",
    specialization: "",
    qualifications: [],
    researchInterests: [],
    professionalMemberships: [],
    achievements: [],
    email: "",
    academicProfile:
      "Additional academic profile information will be updated when supplied by the department.",
  },
  {
    id: 8,
    name: "Mr. Alfa Muhammad",
    role: "Academic Staff",
    image: "/images/staff8.jpg",
    badge: "Staff",
    specialization: "",
    qualifications: [],
    researchInterests: [],
    professionalMemberships: [],
    achievements: [],
    email: "",
    academicProfile:
      "Additional academic profile information will be updated when supplied by the department.",
  },
  {
    id: 9,
    name: "Mr. Baba Iko-Ojo Gideon",
    role: "Academic Staff",
    image: "/images/staff9.jpg",
    badge: "Staff",
    specialization: "",
    qualifications: [],
    researchInterests: [],
    professionalMemberships: [],
    achievements: [],
    email: "",
    academicProfile:
      "Additional academic profile information will be updated when supplied by the department.",
  },
];

/* =========================================================
   DEPARTMENT CONTENT
========================================================= */

const cybersecurityAreas = [
  {
    title: "Network Security",
    icon: Network,
    description:
      "Protecting computer networks from intruders, targeted attacks and opportunistic malware.",
  },
  {
    title: "Cloud Security",
    icon: Cloud,
    description:
      "Protecting cloud-based systems, services, applications and information from internal and external threats.",
  },
  {
    title: "Operational Security",
    icon: Shield,
    description:
      "Managing the processes, permissions and decisions involved in handling and protecting data assets.",
  },
  {
    title: "Application Security",
    icon: Code2,
    description:
      "Keeping software and devices secure from vulnerabilities and threats throughout the application lifecycle.",
  },
  {
    title: "Information Security",
    icon: Database,
    description:
      "Protecting the integrity, confidentiality and privacy of information in storage and in transit.",
  },
  {
    title: "Disaster Recovery",
    icon: HardDrive,
    description:
      "Planning how organisations restore operations and information following security incidents or disruption.",
  },
  {
    title: "Digital Forensics",
    icon: Search,
    description:
      "Gathering, preserving and analysing digital evidence for cybercrime investigation and security analysis.",
  },
  {
    title: "End-user Education",
    icon: Users2,
    description:
      "Teaching safe digital practices that reduce human-related cybersecurity risks within organisations.",
  },
];

const whyStudyItems = [
  "Develop practical knowledge in cybersecurity, information security and digital protection.",
  "Gain skills relevant to the rapidly changing digital environment.",
  "Learn through practical activities, projects and technology-focused training.",
  "Build problem-solving, analytical and critical-thinking skills.",
  "Prepare for careers in cybersecurity, networking, information security and related fields.",
  "Develop professional awareness needed to protect digital systems and information.",
];

const technicalSkills = [
  "Threat Intelligence",
  "Security Principles",
  "Network Security",
  "Endpoint Security Concepts",
  "Vulnerability Management",
  "Risk Management",
  "Incident Handling",
  "Basic Programming & Scripting",
];

const professionalSkills = [
  "Critical Thinking",
  "Attention to Detail",
  "Problem Solving",
  "Communication",
  "Analytical Thinking",
  "Ethical Awareness",
  "Continuous Learning",
];

const admissionRequirements = [
  "Meet the institution's admission requirements for the programme.",
  "Possess the required secondary school qualifications.",
  "Satisfy the relevant subject requirements for the programme.",
  "Complete the required admission and registration processes.",
  "Provide all necessary academic and identification documents.",
];

const cybersecurityJobs = [
  "Cybersecurity Analyst",
  "Network Security Specialist",
  "Security Operations Centre (SOC) Analyst",
  "Cybersecurity Consultant",
  "Digital Forensics Specialist",
  "Security Architect",
  "Penetration Tester / Ethical Hacker",
];

const informationProtectionJobs = [
  "Information Security Officer",
  "IT Security Administrator",
  "Security Systems Administrator",
  "Risk and Compliance Analyst",
  "Cybersecurity Support Specialist",
  "Chief Information Security Officer (CISO)",
];

const certificationsLeft = [
  "CompTIA Security+",
  "Certified Ethical Hacker (CEH)",
  "Cisco Certified CyberOps Associate",
];

const certificationsRight = [
  "Certified Information Systems Security Professional (CISSP)",
  "Certified Information Security Manager (CISM)",
  "Other relevant cybersecurity and professional training programmes",
];

const galleryImages = [
  {
    img: "cyber-staff-group.jpg",
    title: "Departmental Staff",
    caption:
      "Staff of the Department of Cyber Security and Data Protection, Federal Polytechnic Bida.",
    featured: true,
  },
  {
    img: "gallery1.jpg",
    title: "Department Gallery",
    caption: "Departmental activity and engagement.",
  },
  {
    img: "gallery2.jpg",
    title: "Department Gallery",
    caption: "Learning and departmental activities.",
  },
  {
    img: "gallery3.jpg",
    title: "Department Gallery",
    caption: "Cybersecurity education and practical activities.",
  },
  {
    img: "gallery4.jpg",
    title: "Department Gallery",
    caption: "Departmental events and activities.",
  },
];

/* =========================================================
   STAFF ACADEMIC PROFILE MODAL
========================================================= */

const StaffAcademicProfileModal = ({ staff, onClose }) => {
  if (!staff) return null;

  const hasAcademicDetails =
    Boolean(staff.specialization) ||
    Boolean(staff.email) ||
    staff.qualifications?.length > 0 ||
    staff.researchInterests?.length > 0 ||
    staff.professionalMemberships?.length > 0 ||
    staff.achievements?.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/75 backdrop-blur-sm p-3 sm:p-5 md:p-8"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 25 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 20 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="staff-academic-profile-title"
        className="relative w-full max-w-5xl max-h-[92vh] overflow-y-auto rounded-3xl bg-white shadow-2xl"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close staff academic profile"
          className="absolute right-4 top-4 z-30 flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-lg transition hover:bg-slate-100 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600"
        >
          <X size={21} />
        </button>

        <div className="grid md:grid-cols-[320px_minmax(0,1fr)]">
          <aside className="relative overflow-hidden bg-gradient-to-b from-blue-950 via-blue-900 to-blue-800 p-7 pt-16 text-white md:p-8">
            <div className="relative z-10">
              <div className="mx-auto aspect-[4/5] w-full max-w-[270px] overflow-hidden rounded-2xl border-4 border-white/20 bg-slate-200 shadow-2xl">
                <img
                  src={staff.image}
                  alt={staff.name}
                  className="h-full w-full object-cover object-top"
                  onError={(event) => {
                    event.currentTarget.src = "/images/default-staff.jpg";
                  }}
                />
              </div>

              <div className="mt-5 flex justify-center">
                <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-semibold text-blue-50">
                  <BadgeCheck size={15} />
                  {staff.badge}
                </span>
              </div>

              <div className="mt-6 text-center md:text-left">
                <h2 className="text-2xl font-extrabold leading-tight">
                  {staff.name}
                </h2>
                <p className="mt-2 text-sm font-medium text-blue-200">
                  {staff.role}
                </p>
              </div>

              {staff.specialization && (
                <div className="mt-6 flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 p-4">
                  <BriefcaseBusiness
                    size={19}
                    className="mt-0.5 shrink-0 text-blue-300"
                  />
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-blue-300">
                      Area of Specialization
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-white">
                      {staff.specialization}
                    </p>
                  </div>
                </div>
              )}

              {staff.email && (
                <a
                  href={`mailto:${staff.email}`}
                  className="mt-4 flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-blue-100 transition hover:bg-white/10"
                >
                  <Mail size={18} className="shrink-0 text-blue-300" />
                  <span className="break-all">{staff.email}</span>
                </a>
              )}
            </div>
          </aside>

          <div className="min-w-0 p-6 sm:p-8 md:p-10">
            <section>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
                Department Staff
              </p>
              <h3
                id="staff-academic-profile-title"
                className="mt-2 text-2xl font-extrabold text-slate-900 sm:text-3xl"
              >
                Staff Academic Profile
              </h3>
              <div className="mt-4 h-1 w-16 rounded-full bg-blue-700" />
              <p className="mt-6 text-justify leading-7 text-slate-600">
                {staff.academicProfile}
              </p>
            </section>

            {staff.qualifications?.length > 0 && (
              <section className="mt-8">
                <h4 className="flex items-center gap-3 text-lg font-bold text-slate-900">
                  <GraduationCap size={20} className="text-blue-700" />
                  Academic Qualifications
                </h4>
                <div className="mt-4 space-y-3">
                  {staff.qualifications.map((qualification, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3"
                    >
                      <CheckCircle2
                        size={17}
                        className="mt-1 shrink-0 text-blue-700"
                      />
                      <p className="text-sm leading-6 text-slate-700">
                        {qualification}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {staff.researchInterests?.length > 0 && (
              <section className="mt-8">
                <h4 className="flex items-center gap-3 text-lg font-bold text-slate-900">
                  <BookOpenCheck size={19} className="text-blue-700" />
                  Research Interests
                </h4>
                <div className="mt-4 flex flex-wrap gap-2">
                  {staff.researchInterests.map((interest, index) => (
                    <span
                      key={index}
                      className="rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-800"
                    >
                      {interest}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {staff.professionalMemberships?.length > 0 && (
              <section className="mt-8">
                <h4 className="flex items-center gap-3 text-lg font-bold text-slate-900">
                  <Shield size={19} className="text-blue-700" />
                  Professional Memberships
                </h4>
                <div className="mt-4 space-y-3">
                  {staff.professionalMemberships.map((membership, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 rounded-xl border border-slate-200 px-4 py-3"
                    >
                      <BadgeCheck
                        size={18}
                        className="mt-0.5 shrink-0 text-blue-700"
                      />
                      <span className="text-sm leading-6 text-slate-700">
                        {membership}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {staff.achievements?.length > 0 && (
              <section className="mt-8">
                <h4 className="flex items-center gap-3 text-lg font-bold text-slate-900">
                  <Award size={19} className="text-amber-600" />
                  Academic & Professional Achievements
                </h4>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {staff.achievements.map((achievement, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 rounded-xl border border-amber-100 bg-amber-50/70 p-4"
                    >
                      <Award
                        size={18}
                        className="mt-0.5 shrink-0 text-amber-600"
                      />
                      <span className="text-sm font-medium leading-6 text-slate-700">
                        {achievement}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {!hasAcademicDetails && (
              <div className="mt-8 rounded-2xl border border-blue-100 bg-blue-50 p-5">
                <div className="flex items-start gap-3">
                  <Users
                    size={20}
                    className="mt-0.5 shrink-0 text-blue-700"
                  />
                  <div>
                    <h4 className="font-semibold text-blue-950">
                      Additional Academic Information
                    </h4>
                    <p className="mt-1 text-sm leading-6 text-blue-800">
                      Academic qualifications, area of specialization,
                      research interests, professional memberships and
                      achievements will appear here when supplied by the
                      department.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* =========================================================
   GALLERY LIGHTBOX
========================================================= */

const GalleryLightbox = ({ image, images, onClose, onChange }) => {
  if (!image) return null;

  const currentIndex = image.index;

  const showPrevious = () => {
    const previousIndex =
      currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    onChange({ ...images[previousIndex], index: previousIndex });
  };

  const showNext = () => {
    const nextIndex =
      currentIndex === images.length - 1 ? 0 : currentIndex + 1;
    onChange({ ...images[nextIndex], index: nextIndex });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/95 p-3 backdrop-blur-md sm:p-5 md:p-8"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ duration: 0.25 }}
        role="dialog"
        aria-modal="true"
        aria-label="Department gallery image viewer"
        className="relative w-full max-w-6xl"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close gallery"
          className="absolute right-3 top-3 z-30 flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-800 shadow-xl transition hover:bg-blue-50 hover:text-blue-700"
        >
          <X size={22} />
        </button>

        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={showPrevious}
              aria-label="Previous image"
              className="absolute left-3 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-slate-800 shadow-xl transition hover:text-blue-700 md:left-5"
            >
              <ChevronLeft size={27} />
            </button>

            <button
              type="button"
              onClick={showNext}
              aria-label="Next image"
              className="absolute right-3 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-slate-800 shadow-xl transition hover:text-blue-700 md:right-5"
            >
              <ChevronRight size={27} />
            </button>
          </>
        )}

        <div className="overflow-hidden rounded-2xl bg-black shadow-2xl">
          <div className="flex min-h-[260px] items-center justify-center bg-black">
            <motion.img
              key={image.img}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
              src={`/images/${image.img}`}
              alt={image.title}
              className="max-h-[78vh] w-full object-contain"
            />
          </div>

          <div className="flex flex-col gap-3 bg-slate-900 px-5 py-4 text-white sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-semibold">{image.title}</h3>
              {image.caption && (
                <p className="mt-1 max-w-2xl text-sm text-slate-300">
                  {image.caption}
                </p>
              )}
              <p className="mt-1 text-xs text-slate-500">
                Image {currentIndex + 1} of {images.length}
              </p>
            </div>

            <p className="text-xs text-slate-400 sm:text-right">
              Cyber Security & Data Protection
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* =========================================================
   MAIN COMPONENT — NETWORKING & CLOUD UI/UX STYLE
========================================================= */

const CyberSecurity = () => {
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedGalleryImage, setSelectedGalleryImage] = useState(null);

  const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, ease: "easeOut" },
    },
  };

  useEffect(() => {
    const overlayOpen = selectedStaff || selectedGalleryImage;
    if (!overlayOpen) return undefined;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setSelectedStaff(null);
        setSelectedGalleryImage(null);
        return;
      }

      if (selectedGalleryImage && event.key === "ArrowLeft") {
        const previousIndex =
          selectedGalleryImage.index === 0
            ? galleryImages.length - 1
            : selectedGalleryImage.index - 1;
        setSelectedGalleryImage({
          ...galleryImages[previousIndex],
          index: previousIndex,
        });
      }

      if (selectedGalleryImage && event.key === "ArrowRight") {
        const nextIndex =
          selectedGalleryImage.index === galleryImages.length - 1
            ? 0
            : selectedGalleryImage.index + 1;
        setSelectedGalleryImage({
          ...galleryImages[nextIndex],
          index: nextIndex,
        });
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedStaff, selectedGalleryImage]);

  return (
    <div className="min-h-screen bg-gray-50 text-slate-900">
      {/* =====================================================
          DEPARTMENT SUB NAVIGATION
      ===================================================== */}

      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md shadow-md py-3 px-4 md:px-20 flex gap-6 items-center overflow-x-auto whitespace-nowrap">
        <div className="flex-1 font-semibold text-blue-700">
          Cyber Security & Data Protection — Federal Polytechnic Bida
        </div>

        <div className="flex gap-4 text-sm">
          <a href="#brief" className="hover:text-blue-500">
            Overview
          </a>
          <a href="#vision" className="hover:text-blue-500">
            Vision & Mission
          </a>
          <a href="#areas" className="hover:text-blue-500">
            Cyber Areas
          </a>
          <a href="#why" className="hover:text-blue-500">
            Why Study
          </a>
          <a href="#skills" className="hover:text-blue-500">
            Skills
          </a>
          <a href="#admission" className="hover:text-blue-500">
            Admission
          </a>
          <a href="#prospects" className="hover:text-blue-500">
            Careers
          </a>
          <a href="#certifications" className="hover:text-blue-500">
            Certifications
          </a>
          <a href="#staff" className="hover:text-blue-500">
            Meet Staff
          </a>
          <a href="#gallery" className="hover:text-blue-500">
            Gallery
          </a>
          <a href="#contact" className="hover:text-blue-500">
            Contact
          </a>
        </div>
      </nav>

      {/* =====================================================
          HERO — SAME SCALE AS NETWORKING PAGE
      ===================================================== */}

      <header className="relative h-[340px] md:h-[420px] flex items-center justify-center overflow-hidden">
        <img
          src="/images/Cyber1.jpg"
          alt="Department of Cyber Security and Data Protection"
          className="absolute inset-0 w-full h-full object-cover scale-105"
        />

        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-blue-950/60 to-black/35" />

        <div className="relative z-10 text-center text-white px-6">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-3xl md:text-5xl font-extrabold"
          >
            Department of Cyber Security & Data Protection
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mt-3 max-w-3xl mx-auto text-sm md:text-base"
          >
            Developing skilled, ethical and innovative professionals equipped
            to protect digital systems, networks, information and data in an
            increasingly connected world.
          </motion.p>

          <div className="mt-6 flex flex-wrap justify-center gap-4">
            <a
              href="admissions"
              className="inline-flex items-center gap-2 bg-blue-700 text-white px-5 py-3 rounded-lg shadow hover:bg-blue-600 transition"
            >
              Apply Now
            </a>

            <a
              href="#staff"
              className="inline-flex items-center gap-2 bg-white text-blue-700 px-5 py-3 rounded-lg shadow hover:bg-blue-50 transition"
            >
              <Users2 size={18} />
              Meet Our Staff
            </a>
          </div>
        </div>
      </header>

      {/* =====================================================
          MAIN — SAME WIDTH / SPACING AS NETWORKING PAGE
      ===================================================== */}

      <main className="px-6 md:px-20 py-12 space-y-12">
        {/* =================================================
            DEPARTMENT BRIEF
        ================================================= */}

        <motion.section
          id="brief"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="bg-white rounded-2xl shadow-lg p-8 scroll-mt-24"
        >
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <BookOpen className="text-blue-700" />
            The Department in Brief
          </h2>

          <Divider />

          <p className="text-gray-700 text-justify leading-relaxed">
            Cybersecurity is the practice of protecting computers, networks,
            programmes and data from digital attacks, malicious activities,
            data breaches and unauthorised access. It brings together the
            technologies, processes and professional practices needed to
            support the confidentiality, integrity and availability of
            information systems and electronic data.
          </p>

          <p className="text-gray-700 text-justify leading-relaxed mt-4">
            As individuals, organisations and public institutions increasingly
            rely on digital technologies for communication, transactions,
            service delivery and information management, the need for skilled
            cybersecurity professionals continues to grow. The Department of
            Cyber Security and Data Protection is committed to developing
            students with relevant knowledge, practical abilities, analytical
            thinking, ethical awareness and professional competence for the
            protection of systems, information assets and organisational data.
          </p>
        </motion.section>

        {/* =================================================
            HOD SPEECH — NETWORKING STYLE
        ================================================= */}

        <motion.section
          id="hodspeech"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="relative bg-white rounded-2xl shadow-lg p-8 flex flex-col md:flex-row gap-10 items-start overflow-hidden scroll-mt-24"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-white via-blue-50/40 to-white pointer-events-none" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,0,0,0.07),transparent_70%)] pointer-events-none" />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 0.6, y: 0 }}
            transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
            className="absolute top-10 right-10 w-10 h-10 bg-blue-400/40 rounded-full blur-xl"
          />

          <div className="w-full md:w-1/3 flex justify-center relative z-10">
            <motion.img
              src="/images/hodcy.jpeg"
              alt="Mrs. Zainab Ndatsu, Head of Department"
              className="w-52 h-52 rounded-full object-cover object-top shadow-xl border-4 border-white"
              whileHover={{ rotate: 2, scale: 1.04 }}
              transition={{ type: "spring", stiffness: 200, damping: 12 }}
            />
          </div>

          <div className="flex-1 relative z-10">
            <h2 className="text-2xl font-bold flex items-center gap-3">
              <Users className="text-blue-700" />
              HOD Speech
            </h2>

            <Divider />

            <div className="flex flex-wrap gap-3 mb-6">
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium shadow-sm">
                Cybersecurity Education
              </span>
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium shadow-sm">
                Digital Protection
              </span>
              <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium shadow-sm">
                Practical Learning
              </span>
              <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium shadow-sm">
                Professional Development
              </span>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded-md shadow-sm mb-6">
              <p className="italic text-blue-900 font-medium">
                “We encourage our students to remain curious, innovative,
                ethical and committed to continuous learning.”
              </p>
            </div>

            <p className="text-gray-700 text-justify leading-relaxed">
              Dear students, parents, staff and stakeholders, it is my pleasure
              to welcome you to the Department of Cyber Security and Data
              Protection. Our department is committed to providing an academic
              environment where students can acquire relevant knowledge,
              practical skills, discipline and professional values.
            </p>

            <p className="text-gray-700 mt-4 text-justify leading-relaxed">
              As technology continues to transform our society, the need for
              competent professionals who understand cybersecurity,
              information security and digital protection continues to grow.
              We encourage our students to remain curious, innovative, ethical
              and committed to continuous learning.
            </p>

            <p className="text-gray-700 mt-4 text-justify leading-relaxed">
              Together, we can build a generation of professionals capable of
              contributing positively to the security and development of our
              digital society.
            </p>

            <p className="text-blue-700 font-semibold mt-6">
              — Mrs. Zainab Ndatsu
            </p>
            <p className="text-gray-600">Head of Department</p>
          </div>
        </motion.section>

        {/* =================================================
            VISION & MISSION
        ================================================= */}

        <motion.section
          id="vision"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="bg-white rounded-2xl shadow-lg p-8 scroll-mt-24"
        >
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <Shield className="text-blue-700" />
            Vision & Mission
          </h2>

          <Divider />

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center">
                  <Award size={19} />
                </div>
                <h3 className="text-lg font-bold text-slate-900">Our Vision</h3>
              </div>

              <p className="text-gray-700 leading-relaxed text-justify">
                To be a leading centre of excellence in cybersecurity
                education, practical training, research, innovation and
                professional development, producing competent and responsible
                graduates capable of contributing to the protection of digital
                systems and information.
              </p>
            </div>

            <div className="bg-blue-900 text-white rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-white/15 text-white flex items-center justify-center">
                  <CheckCircle2 size={19} />
                </div>
                <h3 className="text-lg font-bold">Our Mission</h3>
              </div>

              <p className="text-blue-100 leading-relaxed text-justify">
                To provide quality education and practical training that equips
                students with technical knowledge, cybersecurity skills,
                ethical values, analytical abilities and professional
                competencies required to respond effectively to emerging
                digital security challenges.
              </p>
            </div>
          </div>
        </motion.section>

        {/* =================================================
            MAJOR AREAS OF CYBERSECURITY
        ================================================= */}

        <motion.section
          id="areas"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="bg-white rounded-2xl shadow-lg p-8 scroll-mt-24"
        >
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <Lock className="text-blue-700" />
            Major Areas of Cybersecurity
          </h2>

          <Divider />

          <p className="text-gray-700 leading-relaxed mb-6">
            Cybersecurity covers several interconnected areas concerned with
            protecting networks, applications, information, operations and
            people from digital threats.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {cybersecurityAreas.map((area) => {
              const Icon = area.icon;

              return (
                <motion.div
                  key={area.title}
                  whileHover={{ y: -4 }}
                  className="bg-gray-50 rounded-xl p-5 border border-gray-100 hover:border-blue-200 hover:shadow-md transition"
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center">
                    <Icon size={19} />
                  </div>
                  <h3 className="mt-4 font-semibold text-slate-900">
                    {area.title}
                  </h3>
                  <p className="mt-2 text-sm text-gray-600 leading-6">
                    {area.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </motion.section>

        {/* =================================================
            WHY STUDY HERE — SAME BLUE SECTION AS NETWORKING
        ================================================= */}

        <motion.section
          id="why"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="bg-blue-900 text-white rounded-2xl shadow-lg p-8 scroll-mt-24"
        >
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <Award className="text-yellow-300" />
            Why Study Cyber Security & Data Protection?
          </h2>

          <Divider />

          <p className="text-gray-100 text-justify leading-relaxed">
            Cybersecurity education equips students with the knowledge,
            practical skills and professional awareness needed to understand,
            protect and secure modern digital systems and information in a
            technology-driven world.
          </p>

          <ul className="mt-6 grid md:grid-cols-2 gap-3 text-gray-100">
            {whyStudyItems.map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <CheckCircle2 className="mt-1 shrink-0" size={19} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </motion.section>

        {/* =================================================
            SKILLS YOU WILL DEVELOP
        ================================================= */}

        <motion.section
          id="skills"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="bg-white rounded-2xl shadow-lg p-8 scroll-mt-24"
        >
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <FileKey2 className="text-blue-700" />
            Skills You Will Develop
          </h2>

          <Divider />

          <p className="text-gray-700 leading-relaxed mb-6">
            Cybersecurity requires a combination of technical knowledge,
            analytical ability, attention to detail, communication and a
            commitment to continuous learning.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold text-lg text-blue-700 flex items-center gap-2 mb-4">
                <Server size={19} />
                Technical Skills
              </h3>
              <ul className="grid sm:grid-cols-2 gap-3 text-gray-700">
                {technicalSkills.map((skill) => (
                  <li key={skill} className="flex items-start gap-2">
                    <CheckCircle2
                      size={17}
                      className="mt-0.5 shrink-0 text-blue-700"
                    />
                    <span>{skill}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-blue-50 rounded-xl p-6">
              <h3 className="font-semibold text-lg text-blue-700 flex items-center gap-2 mb-4">
                <Users size={19} />
                Professional Skills
              </h3>
              <ul className="grid sm:grid-cols-2 gap-3 text-gray-700">
                {professionalSkills.map((skill) => (
                  <li key={skill} className="flex items-start gap-2">
                    <CheckCircle2
                      size={17}
                      className="mt-0.5 shrink-0 text-blue-700"
                    />
                    <span>{skill}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.section>

        {/* =================================================
            ADMISSION — NETWORKING STYLE WHITE CARD
        ================================================= */}

        <motion.section
          id="admission"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="bg-white rounded-2xl shadow-lg p-8 scroll-mt-24"
        >
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <Users className="text-blue-700" />
            Admission Requirements
          </h2>

          <Divider />

          <p className="text-gray-700 text-justify leading-relaxed">
            Prospective students should satisfy the relevant institutional and
            programme requirements before applying for admission.
          </p>

          <div className="mt-6 grid md:grid-cols-2 gap-3">
            {admissionRequirements.map((item, index) => (
              <div
                key={index}
                className="flex items-start gap-3 bg-gray-50 rounded-lg p-4 border border-gray-100"
              >
                <CheckCircle2
                  size={18}
                  className="mt-0.5 shrink-0 text-blue-700"
                />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </motion.section>

        {/* =================================================
            JOB PROSPECTS — NETWORKING TWO-COLUMN FORMAT
        ================================================= */}

        <motion.section
          id="prospects"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="bg-white rounded-2xl shadow-lg p-8 scroll-mt-24"
        >
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <BriefcaseBusiness className="text-blue-700" />
            Job Prospects
          </h2>

          <Divider />

          <p className="text-gray-700 text-justify leading-relaxed">
            Cybersecurity professionals are needed across public and private
            organisations that depend on secure digital systems, information,
            applications and networks.
          </p>

          <div className="mt-6 grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-blue-700 mb-2">
                Cybersecurity & Technical Roles
              </h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                {cybersecurityJobs.map((job) => (
                  <li key={job}>{job}</li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-blue-700 mb-2">
                Information Protection & Management Roles
              </h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                {informationProtectionJobs.map((job) => (
                  <li key={job}>{job}</li>
                ))}
              </ul>
            </div>
          </div>
        </motion.section>

        {/* =================================================
            CERTIFICATIONS — NETWORKING LIGHT BLUE CARD
        ================================================= */}

        <motion.section
          id="certifications"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="bg-blue-50 rounded-2xl shadow-lg p-8 scroll-mt-24"
        >
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <Shield className="text-blue-700" />
            Certification / Professional Courses
          </h2>

          <Divider />

          <p className="text-gray-800 text-justify leading-relaxed">
            Relevant industry certifications can complement academic learning,
            strengthen professional credibility and support continuous
            development in cybersecurity and information security.
          </p>

          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-blue-700 mb-2">
                Security & Ethical Hacking
              </h4>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                {certificationsLeft.map((certification) => (
                  <li key={certification}>{certification}</li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-blue-700 mb-2">
                Advanced Security & Management
              </h4>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                {certificationsRight.map((certification) => (
                  <li key={certification}>{certification}</li>
                ))}
              </ul>
            </div>
          </div>
        </motion.section>

        {/* =================================================
            MEET OUR STAFF — NETWORKING CARD STYLE
        ================================================= */}

        <motion.section
          id="staff"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="bg-white rounded-2xl shadow-lg p-6 md:p-8 scroll-mt-24"
        >
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-3 text-slate-900">
              <Users2 className="text-blue-700" />
              Meet Our Staff
            </h2>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-600 sm:text-base">
              Meet the academic professionals contributing to teaching,
              learning, student development and the growth of the Department of
              Cyber Security and Data Protection.
            </p>
          </div>

          <Divider />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {staffList.map((staff) => (
              <motion.button
                type="button"
                key={staff.id}
                onClick={() => setSelectedStaff(staff)}
                whileHover={{ y: -7 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
                aria-label={`View academic profile of ${staff.name}`}
                className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white text-left shadow-sm transition-all duration-300 hover:border-blue-200 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
              >
                <div className="relative h-[310px] overflow-hidden bg-slate-100">
                  <img
                    src={staff.image}
                    alt={staff.name}
                    loading="lazy"
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    onError={(event) => {
                      event.currentTarget.src = "/images/default-staff.jpg";
                    }}
                  />

                  <div className="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-slate-950 via-slate-950/65 to-transparent" />

                  <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-blue-700 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-white shadow-lg">
                    {staff.badge === "HOD" && <BadgeCheck size={14} />}
                    {staff.badge}
                  </span>

                  <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                    <h3 className="text-lg font-bold leading-tight">
                      {staff.name}
                    </h3>
                    <p className="mt-1.5 text-sm font-medium text-blue-200">
                      {staff.role}
                    </p>
                  </div>
                </div>

                <div className="p-5">
                  <div className="flex items-center justify-between border-t border-slate-100 pt-4 text-sm font-bold text-blue-700">
                    <span>View Academic Profile</span>
                    <ChevronRight
                      size={19}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </div>
                </div>
              </motion.button>
            ))}
          </div>

          <p className="mt-6 text-center text-xs text-slate-500">
            Click or tap any staff card to view the staff academic profile.
          </p>
        </motion.section>

        {/* =================================================
            GALLERY — NETWORKING EQUAL CARD STYLE
        ================================================= */}

        <motion.section
          id="gallery"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="bg-white rounded-2xl shadow-lg p-6 md:p-8 scroll-mt-24"
        >
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-3">
              <Cloud className="text-blue-700" />
              Department Gallery
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 sm:text-base">
              Explore staff, activities, learning experiences, facilities and
              departmental events.
            </p>
          </div>

          <Divider />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {galleryImages.map((image, index) => (
              <motion.button
                type="button"
                key={image.img}
                onClick={() =>
                  setSelectedGalleryImage({ ...image, index })
                }
                whileHover={{ y: -5 }}
                whileTap={{ scale: 0.98 }}
                aria-label={`View ${image.title}`}
                className="group relative overflow-hidden rounded-xl border border-slate-200 bg-slate-100 text-left shadow-sm transition hover:border-blue-200 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={`/images/${image.img}`}
                    alt={image.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                    onError={(event) => {
                      event.currentTarget.src = "/images/Cyber1.jpg";
                    }}
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/5 to-transparent" />

                  {image.featured && (
                    <span className="absolute left-3 top-3 rounded-full bg-blue-700 px-3 py-1 text-[11px] font-semibold text-white shadow">
                      Featured
                    </span>
                  )}

                  <div className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-blue-700 shadow-lg transition group-hover:scale-110">
                    <Maximize2 size={17} />
                  </div>

                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="rounded-full bg-white/95 px-4 py-2 text-sm font-semibold text-blue-800 opacity-0 shadow-lg transition-all duration-300 group-hover:opacity-100">
                      Click to View
                    </span>
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    <p className="text-sm font-semibold">{image.title}</p>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>

          <p className="mt-5 text-center text-xs text-slate-500">
            Click or tap any photograph to view the full image.
          </p>
        </motion.section>

        {/* =================================================
            CONTACT + MAP — ONE NETWORKING STYLE CARD
        ================================================= */}

        <motion.section
          id="contact"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="bg-white rounded-2xl shadow-lg p-8 scroll-mt-24"
        >
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <MapPin className="text-blue-700" />
            Contact the Department
          </h2>

          <Divider />

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-gray-700">
                <strong>Address:</strong> Federal Polytechnic Bida, Niger State,
                Nigeria
              </p>

              <p className="text-gray-700 flex items-center gap-2 mt-3">
                <Mail className="text-blue-700" size={19} />
                dept.cybersecurity@fedpolybida.edu.ng
              </p>

              <p className="text-gray-700 flex items-center gap-2 mt-3">
                <Phone className="text-blue-700" size={19} />
                +234 803 610 8345
              </p>

              <div className="mt-6 rounded-xl bg-blue-50 border border-blue-100 p-4">
                <p className="font-semibold text-blue-900">
                  Department of Cyber Security & Data Protection
                </p>
                <p className="mt-1 text-sm leading-6 text-blue-800">
                  Federal Polytechnic Bida, Niger State, Nigeria.
                </p>
              </div>
            </div>

            <div>
              <iframe
                title="Federal Polytechnic Bida location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3931.203503579147!2d6.0079231!3d9.0391016!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x104966b7f2e9b195%3A0x18c8eec7a2fae37c!2sFederal+Polytechnic+Bida%2C+Doko+Road%2C+Bida%2C+Niger+State!5e0!3m2!1sen!2sus!4v1694784000000!5m2!1sen!2sus"
                width="100%"
                height="220"
                loading="lazy"
                className="rounded-md border-0 shadow-sm"
              />
            </div>
          </div>
        </motion.section>
      </main>

      {/* =====================================================
          STAFF ACADEMIC PROFILE MODAL
      ===================================================== */}

      <AnimatePresence>
        {selectedStaff && (
          <StaffAcademicProfileModal
            key={`staff-${selectedStaff.id}`}
            staff={selectedStaff}
            onClose={() => setSelectedStaff(null)}
          />
        )}
      </AnimatePresence>

      {/* =====================================================
          GALLERY LIGHTBOX
      ===================================================== */}

      <AnimatePresence>
        {selectedGalleryImage && (
          <GalleryLightbox
            key="cyber-gallery-lightbox"
            image={selectedGalleryImage}
            images={galleryImages}
            onClose={() => setSelectedGalleryImage(null)}
            onChange={setSelectedGalleryImage}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default CyberSecurity;

